import React, { useState, useEffect, useCallback } from 'react';
import { getSeverityIcon, getRecIcon, formatTimestamp } from '../../utils/aiHelpers';
import './TVAIWidget.css';

const API_URL = process.env.REACT_APP_API_URL || '';

function TVAIWidget({ activeLocation }) {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [agentStatus, setAgentStatus] = useState('checking');

  // Check AI agent health on mount
  useEffect(() => {
    const checkHealth = async () => {
      const url = `${API_URL}/api/ai/health`;
      try {
        const res = await fetch(url);
        if (res.ok) {
          const data = await res.json();
          setAgentStatus(data.status);
        } else {
          setAgentStatus('fallback');
        }
      } catch {
        setAgentStatus('fallback');
      }
    };
    checkHealth();
  }, []);

  // Fetch AI analysis
  const fetchAnalysis = useCallback(async () => {
    if (!activeLocation) return;

    console.log('[TVAIWidget DEBUG] Fetching analysis for:', activeLocation);
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_URL}/api/ai/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ city: activeLocation }),
      });

      console.log('[TVAIWidget DEBUG] Response status:', res.status);

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || `HTTP ${res.status}`);
      }

      const data = await res.json();
      console.log('[TVAIWidget DEBUG] Full API response:', JSON.stringify(data, null, 2));
      console.log('[TVAIWidget DEBUG] Model used:', data.model);
      console.log('[TVAIWidget DEBUG] Alerts count:', data.alerts?.length || 0);
      console.log('[TVAIWidget DEBUG] Alerts:', data.alerts);
      console.log('[TVAIWidget DEBUG] Briefing:', data.briefing);
      console.log('[TVAIWidget DEBUG] Error message:', data.error_message);
      setAnalysis(data);

      if (data.budget_exceeded) {
        setAgentStatus('budget_exceeded');
      } else if (data.model === 'rule-based-fallback') {
        // Distinguish between degraded (key exists but errors) and plain fallback
        setAgentStatus(data.error_message ? 'degraded' : 'fallback');
      } else {
        setAgentStatus('healthy');
      }
    } catch (err) {
      setError(err.message);
      setAnalysis(null);
    } finally {
      setLoading(false);
    }
  }, [activeLocation]);

  // Auto-fetch when location changes
  useEffect(() => {
    if (activeLocation) {
      fetchAnalysis();
    }
  }, [activeLocation, fetchAnalysis]);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    if (!activeLocation) return;
    const interval = setInterval(() => {
      fetchAnalysis();
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [activeLocation, fetchAnalysis]);

  const alertCount = analysis?.alerts?.length || 0;

  return (
    <div className="tv-ai-widget">
      {/* Header */}
      <div className="tv-ai-header">
        <div className="tv-ai-header-left">
          <div className={`tv-ai-icon ${agentStatus === 'budget_exceeded' ? 'budget-warn' : ''}`}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2a7 7 0 0 1 7 7c0 2.38-1.19 4.47-3 5.74V17a2 2 0 0 1-2 2H10a2 2 0 0 1-2-2v-2.26C6.19 13.47 5 11.38 5 9a7 7 0 0 1 7-7z"/>
              <line x1="10" y1="22" x2="14" y2="22"/>
              <line x1="9" y1="9" x2="15" y2="9"/>
              <line x1="12" y1="6" x2="12" y2="12"/>
            </svg>
          </div>
          <div className="tv-ai-title-group">
            <span className="tv-ai-title">AI Weather Agent</span>
            <span className="tv-ai-subtitle">
              <span className={`tv-ai-status-dot ${agentStatus === 'budget_exceeded' ? 'warning' : agentStatus}`}></span>
              {agentStatus === 'healthy' ? 'Bedrock Connected' :
               agentStatus === 'degraded' ? '⚠️ Bedrock Errors - Rule-Based Mode' :
               agentStatus === 'budget_exceeded' ? 'Budget Limit Reached' :
               agentStatus === 'fallback' ? 'Rule-Based Mode' :
               agentStatus === 'checking' ? 'Connecting...' : 'Offline'}
            </span>
          </div>
        </div>

        <div className="tv-ai-header-right">
          {analysis && (
            alertCount > 0 ? (
              <span className="tv-ai-alert-badge has-alerts">
                ⚠ {alertCount} alert{alertCount !== 1 ? 's' : ''}
              </span>
            ) : (
              <span className="tv-ai-alert-badge no-alerts">
                ✓ Clear
              </span>
            )
          )}
          {loading && (
            <div className="tv-ai-loading-indicator">
              <span></span><span></span><span></span>
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="tv-ai-body">
        {loading && !analysis ? (
          <div className="tv-ai-loading">
            <div className="tv-ai-loading-dots">
              <span></span><span></span><span></span>
            </div>
            Analyzing weather conditions...
          </div>
        ) : error && !analysis ? (
          <div className="tv-ai-error">
            <span className="tv-ai-error-icon">⚠️</span>
            <span>{error}</span>
          </div>
        ) : analysis ? (
          <>
            {/* Briefing */}
            <div className="tv-ai-briefing">
              <div className="tv-ai-briefing-label">Weather Briefing</div>
              <p className="tv-ai-briefing-text">{analysis.briefing}</p>
            </div>

            {/* Alerts */}
            <div className="tv-ai-alerts">
              <div className="tv-ai-alerts-label">
                Alerts {alertCount > 0 && `(${alertCount})`}
              </div>
              {alertCount > 0 ? (
                analysis.alerts.map((alert, i) => (
                  <div key={i} className={`tv-ai-alert-item ${alert.severity}`}>
                    <span className="tv-ai-alert-icon">{getSeverityIcon(alert.severity)}</span>
                    <div className="tv-ai-alert-content">
                      <div className="tv-ai-alert-title">{alert.title}</div>
                      <div className="tv-ai-alert-message">{alert.message}</div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="tv-ai-no-alerts">
                  ✅ No weather alerts – conditions look good!
                </div>
              )}
            </div>

            {/* Recommendations */}
            {analysis.recommendations && analysis.recommendations.length > 0 && (
              <div className="tv-ai-recommendations">
                <div className="tv-ai-recommendations-label">Recommendations</div>
                <div className="tv-ai-rec-list">
                  {analysis.recommendations.map((rec, i) => (
                    <span key={i} className="tv-ai-rec-item">
                      <span className="tv-ai-rec-icon">{getRecIcon(rec)}</span>
                      {rec}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Fallback error notice */}
            {analysis.model === 'rule-based-fallback' && analysis.error_message && (
              <div className="tv-ai-fallback-error">
                <span className="tv-ai-fallback-error-icon">⚠️</span>
                <span className="tv-ai-fallback-error-text">
                  Bedrock unavailable: {analysis.error_message}
                </span>
              </div>
            )}

            {/* Footer */}
            <div className="tv-ai-footer">
              <span>
                {analysis.model === 'rule-based-fallback'
                  ? 'Rule-based analysis'
                  : `Powered by ${analysis.model || 'Amazon Bedrock'}`}
              </span>
              <span>{formatTimestamp(analysis.timestamp)}</span>
            </div>
          </>
        ) : (
          <div className="tv-ai-loading">
            Waiting for location data...
          </div>
        )}
      </div>
    </div>
  );
}

export default TVAIWidget;
